import type {
  ClientSettings,
  ItemPacking,
  ItemType,
  Order,
  VehicleTypes,
  Weight,
} from '@hero/schema';
import { convertWeight } from '@hero/core';

/**
 * Load fields that map onto `Client.settings` (no pickup dates or
 * other order-only fields). Shared by the publish flow and the
 * order-drawer edit-load surface.
 */
export type LoadDefaultsSource = {
  vehicleTypes?: VehicleTypes[];
  itemDescription?: string;
  itemType?: ItemType;
  packing?: ItemPacking;
  quantity?: number;
  /** Weight in tons — same unit as the edit-load / publish form. */
  weightTon?: number;
  itemCost?: number;
  temperatureType?: string;
  temperatureDegree?: { min: number; max: number };
  podSettings?: 'pod_image' | 'return_original_pod';
};

/** CRM Client ObjectId hex from `Order.client.ids[]`, if present. */
export function getClientIdFromOrder(order: Order | null | undefined): string | undefined {
  return order?.client?.ids?.find(entry => entry.type === 'client_id')?.id;
}

/**
 * Project load form values onto a `ClientSettings` patch.
 * Inverse of {@link resolveLoadDefaults} for the overlapping fields.
 */
export function projectLoadIntoClientSettings(source: LoadDefaultsSource): ClientSettings {
  const settings: ClientSettings = {};

  if (source.vehicleTypes?.length) {
    settings.vehicle_preferred_types = source.vehicleTypes;
  }
  if (source.itemType) settings.item_default_type = source.itemType;
  if (source.packing) settings.item_default_packing = source.packing;
  if (typeof source.quantity === 'number' && source.quantity > 0) {
    settings.item_default_quantity = source.quantity;
  }
  if (source.itemDescription?.trim()) {
    settings.item_default_name = source.itemDescription.trim();
  }
  if (typeof source.itemCost === 'number' && source.itemCost > 0) {
    settings.item_default_cost = source.itemCost;
  }
  if (source.podSettings) {
    settings.pod_settings = source.podSettings;
  }
  if (typeof source.weightTon === 'number' && source.weightTon > 0) {
    const weight: Weight = { value: source.weightTon, type: 'ton' };
    settings.item_default_weight = weight;
  }
  if (source.temperatureType) {
    settings.item_default_temperature = {
      types: [source.temperatureType as 'chilled' | 'frozen' | 'hot' | 'dry'],
      degree: source.temperatureDegree,
    };
  }
  return settings;
}

/** Merge load defaults into existing client settings (full sub-doc replace on wire). */
export function mergeClientSettingsWithLoad(
  existing: ClientSettings | undefined,
  loadPatch: ClientSettings,
): ClientSettings {
  return { ...(existing ?? {}), ...loadPatch };
}

function normalizeWeightTon(weight: Weight | undefined): number | undefined {
  if (!weight || !(weight.value > 0)) return undefined;
  return convertWeight(weight, 'ton', { decimals: 3, removeTrailingZeros: true }).value;
}

function sameNumber(a: number | undefined, b: number | undefined): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return Math.abs(a - b) < 1e-6;
}

function sameString(a: string | undefined, b: string | undefined): boolean {
  return (a?.trim() || '') === (b?.trim() || '');
}

function sameVehicleTypes(a: VehicleTypes[] | undefined, b: VehicleTypes[] | undefined): boolean {
  const left = [...(a ?? [])].sort();
  const right = [...(b ?? [])].sort();
  if (left.length !== right.length) return false;
  return left.every((v, i) => v === right[i]);
}

/**
 * True when the load form already matches the client's saved defaults
 * for every overlapping field that has a value on either side.
 */
export function loadMatchesClientSettings(
  source: LoadDefaultsSource,
  settings: ClientSettings | undefined,
): boolean {
  if (!settings) return false;

  if (!sameVehicleTypes(source.vehicleTypes, settings.vehicle_preferred_types)) {
    return false;
  }
  if (!sameString(source.itemDescription, settings.item_default_name)) {
    return false;
  }
  if ((source.itemType ?? undefined) !== (settings.item_default_type ?? undefined)) {
    return false;
  }
  if ((source.packing ?? undefined) !== (settings.item_default_packing ?? undefined)) {
    return false;
  }
  if (!sameNumber(source.quantity, settings.item_default_quantity)) {
    return false;
  }
  if (!sameNumber(source.weightTon, normalizeWeightTon(settings.item_default_weight))) {
    return false;
  }
  if (!sameNumber(source.itemCost, settings.item_default_cost)) {
    return false;
  }
  const sourceTemp = source.temperatureType;
  const settingsTemp = settings.item_default_temperature?.types?.[0];
  if ((sourceTemp ?? undefined) !== (settingsTemp ?? undefined)) {
    return false;
  }
  if ((source.podSettings ?? undefined) !== (settings.pod_settings ?? undefined)) {
    return false;
  }
  return true;
}
